import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TAdBannerCommonParameters } from '../types';
import { generateRandomString } from '../utils/generateRandomString';
import { ESectionType } from '../../../types/sectionType';
import { selectAdfoxRequestedParams } from '../../../selectors/selectAdfoxRequestedParams';
import { selectCountrysideAdfoxPuids } from '../../../selectors/countryside/selectCountrysideAdfoxPuids';
import { selectPageSection } from '../../../selectors/pageSection';
import { selectRegionsData } from '../../../selectors/mainpage/selectRegionsData';

const toPuids = (params: Record<string, unknown> | undefined | null): Record<string, string> =>
  Object.entries(params || {}).reduce<Record<string, string>>((acc, [key, value]) => {
    if (key.startsWith('puid') && typeof value === 'string') {
      acc[key] = value;
    }

    return acc;
  }, {});

/**
 * Резолвит ОБЩИЕ параметры (`puids`, `regionId`, `placeId`) из стейта хоста.
 *
 * Источник puid'ов зависит от секции: на загородке используется собственный слайс
 * `state.countrysideAdfox.puids`, на остальных — `state.adfoxParams`. Это небольшое
 * отступление от «puids — единое общее значение», но оно спрятано внутри общего хука,
 * чтобы place-контейнеры по-прежнему возвращали только place-параметры.
 *
 * `placeId` связывает параметры микрофронта с подпиской на lifecycle-события в host page.
 * Генерируется так же, как instance.id в @cian/mf-registry: 8 hex-символов.
 */
export function useAdBannerCommonParameters(): TAdBannerCommonParameters {
  const placeId = useMemo(() => generateRandomString(), []);
  const section = useSelector(selectPageSection);
  const adfoxRequestedParams = useSelector(selectAdfoxRequestedParams);
  const countrysidePuids = useSelector(selectCountrysideAdfoxPuids);
  const { subdomainCityId, subdomainRegionId } = useSelector(selectRegionsData);

  return useMemo<TAdBannerCommonParameters>(() => {
    const isCountryside = section === ESectionType.Countryside;
    const source = (isCountryside ? countrysidePuids : adfoxRequestedParams) as Record<string, unknown>;

    return {
      puids: toPuids(source),
      regionId: subdomainCityId || subdomainRegionId || 1,
      placeId,
    };
  }, [section, adfoxRequestedParams, countrysidePuids, subdomainCityId, subdomainRegionId, placeId]);
}
